import { useEffect, useMemo, useState } from 'react';
import { captureException } from '@sentry/react';
import { API } from './api';
import { TreatmentKeys } from '../types';

const FALLBACK_SPLIT_TREATMENT = 'off';

export function useSplit(splitName: TreatmentKeys, noCache = false): boolean {
	const [treatment, setTreatment] = useState<string | undefined>();

	useEffect(() => {
		API.getFeatureTreatment(splitName, noCache)
			.then((response) => {
				setTreatment(response.treatment);
			})
			.catch((error) => {
				setTreatment(FALLBACK_SPLIT_TREATMENT);
				console.error(
					`Something went wrong while fetching the ${splitName} Split treatment`,
					error,
				);
				captureException(error);
			});
	}, [splitName, noCache]);

	return treatment === 'on';
}

export function useMemoizedSplit(splitKey: TreatmentKeys) {
	const splitValue = useSplit(splitKey);

	return useMemo(() => splitValue, [splitValue]);
}

export function useSplitWithMountCheck(splitName: TreatmentKeys, noCache = false): boolean | null {
	const [treatment, setTreatment] = useState<string | undefined>(undefined);

	useEffect(() => {
		let isMounted = true;

		(async () => {
			try {
				const response = await API.getFeatureTreatment(splitName, noCache);

				if (isMounted) {
					setTreatment(response.treatment);
				}
			} catch (error) {
				if (isMounted) {
					setTreatment(FALLBACK_SPLIT_TREATMENT);
				}

				console.error(
					`Something went wrong while fetching the ${splitName} Split treatment`,
					error,
				);
				captureException(error);
			}
		})();

		return () => {
			isMounted = false;
		};
	}, [splitName, noCache]);

	if (treatment === undefined) {
		return null;
	}

	return treatment === 'on';
}
